import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  query,
  writeBatch,
  setDoc,
} from "firebase/firestore";
import {
  runtimeDb as db,
  hasActiveRuntime,
  getActiveRuntimeConnectionId,
  getRuntimeDb,
  requireCustomerRuntime,
} from "../features/firestoreStudio/runtime/runtimeDb";
import {
  discoverCollectionsViaAdminSdk,
  DISCOVERY_ENGINE,
  mapDiscoveryToRegistryList,
} from "./collectionDiscoveryService";
import { getConnectionSecrets } from "../features/firestoreStudio/services/connectionService";
import { DISCOVERY_SOURCE } from "../features/firestoreAdmin/constants/discoverySources";
import { logDiscovery } from "../features/firestoreStudio/runtime/runtimeLogger";

export { DISCOVERY_SOURCE };

/** Firestore source of truth for registered root collections. */
export const REGISTRY_COLLECTION = "firestore_collection_registry";

function normalizeName(name) {
  return String(name || "").trim();
}

function registryRef(collectionName) {
  return doc(db, REGISTRY_COLLECTION, normalizeName(collectionName));
}

function runtimeMeta() {
  const firestore = getRuntimeDb();
  return {
    activeConnectionId: getActiveRuntimeConnectionId(),
    runtimeProjectId: firestore?.app?.options?.projectId || null,
    hasActiveRuntime: hasActiveRuntime(),
  };
}

const CUSTOMER_SA_REQUIRED_MSG =
  "Customer project discovery requires a service account on this connection. " +
  "Open Connection Manager → Edit connection → Service Account → upload the customer Firebase service account JSON " +
  "(Cloud Datastore Viewer on the customer project). " +
  "The browser is not signed into the customer Firebase project, so client Firestore access will always fail with " +
  '"Missing or insufficient permissions."';

async function assertCustomerServiceAccount(connectionId) {
  if (!connectionId) {
    throw new Error("No active connection — choose a customer connection in the workspace bar.");
  }
  const conn = await getConnectionSecrets(connectionId);
  if (!conn?.hasServiceAccount) {
    throw new Error(CUSTOMER_SA_REQUIRED_MSG);
  }
  return conn;
}

async function readRegistryCacheOnly() {
  requireCustomerRuntime();
  const snap = await getDocs(collection(db, REGISTRY_COLLECTION));
  return snap.docs
    .map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        collectionName: data.collectionName || d.id,
        createdAt: data.createdAt || null,
        createdBy: data.createdBy || null,
        documentCount: typeof data.documentCount === "number" ? data.documentCount : null,
        lastUpdated: data.lastUpdated || null,
        status: data.status === "empty" ? "empty" : "active",
      };
    })
    .sort((a, b) => a.collectionName.localeCompare(b.collectionName));
}

/**
 * Remove registry docs in the active runtime project that are not in the discovered set.
 * Best-effort only — registry rebuild should run via Cloud Function (Admin SDK).
 */
async function purgeStaleRegistryEntries(discoveredNames) {
  try {
    requireCustomerRuntime();
    const keep = new Set(discoveredNames.map(normalizeName).filter(Boolean));
    const snap = await getDocs(collection(db, REGISTRY_COLLECTION));
    const stale = snap.docs.filter((d) => !keep.has(d.id));
    if (!stale.length) return 0;

    let removed = 0;
    let batch = writeBatch(db);
    let ops = 0;
    for (const docSnap of stale) {
      batch.delete(docSnap.ref);
      ops += 1;
      removed += 1;
      if (ops >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
    }
    if (ops > 0) await batch.commit();
    return removed;
  } catch (err) {
    if (err?.code === "permission-denied") {
      console.warn(
        "[collectionRegistry] registry purge skipped — browser lacks write access; " +
        "use Rediscover (Cloud Function) to rebuild the registry cache."
      );
      return 0;
    }
    throw err;
  }
}

function isPermissionDeniedError(err) {
  const code = err?.code || err?.details?.code;
  const msg = String(err?.message || "");
  return (
    code === "permission-denied" ||
    /missing or insufficient permissions/i.test(msg)
  );
}

function formatDiscoveryError(err) {
  if (isPermissionDeniedError(err)) {
    return (
      "Missing or insufficient permissions for customer project discovery. " +
      "Upload a customer service account with Cloud Datastore Viewer (list) on the connection. " +
      "For registry cache writes, Datastore User is also required — or use discovery without registry sync."
    );
  }
  return err?.message || String(err);
}

/**
 * Discover collections from the active runtime project (source of truth).
 * Optionally writes to registry cache in the same runtime project.
 */
async function discoverFromActiveRuntime({
  createdBy = null,
  syncRegistry = false,
  replaceRegistry = false,
  discoverySource = DISCOVERY_SOURCE.RUNTIME_DISCOVERY,
} = {}) {
  const meta = runtimeMeta();
  requireCustomerRuntime(meta.activeConnectionId);
  await assertCustomerServiceAccount(meta.activeConnectionId);

  const discovery = await discoverCollectionsViaAdminSdk({
    connectionId: meta.activeConnectionId,
    runtimeProjectId: meta.runtimeProjectId,
    syncRegistry,
    createdBy,
  });

  const list = mapDiscoveryToRegistryList(discovery);
  let registryUpdated = Boolean(discovery.registrySynced || discovery.registryWritten);

  // Registry rebuild is handled server-side when syncRegistry=true (Admin SDK).
  // Client purge is best-effort only and skipped when rules block browser writes.
  if (replaceRegistry && !syncRegistry && list.length) {
    const removed = await purgeStaleRegistryEntries(list.map((r) => r.collectionName));
    registryUpdated = registryUpdated || removed > 0;
  }

  logDiscovery({
    discoverySource,
    connectionId: meta.activeConnectionId,
    projectId: meta.runtimeProjectId,
    collectionsDiscovered: list.length,
    collectionsRendered: list.length,
    registryUpdated,
  });

  return {
    list,
    discovery,
    discoverySource,
    registryUpdated,
    meta,
  };
}

/**
 * @returns {Promise<Array<{
 *   id: string,
 *   collectionName: string,
 *   createdAt: string|null,
 *   createdBy: string|null,
 *   documentCount: number|null,
 *   lastUpdated: string|null,
 *   status: 'active'|'empty',
 * }>>}
 */
export async function listRegisteredCollections(options = {}) {
  const meta = runtimeMeta();
  const discoverySource = options.discoverySource || DISCOVERY_SOURCE.RUNTIME_DISCOVERY;
  console.info("[collectionRegistry] listRegisteredCollections", { ...meta, discoverySource });

  if (!meta.hasActiveRuntime) {
    throw new Error("No active workspace connection.");
  }

  try {
    const { list, discovery } = await discoverFromActiveRuntime({
      createdBy: options.createdBy || null,
      syncRegistry: options.syncRegistry === true,
      replaceRegistry: false,
      discoverySource,
    });

    console.info("[collectionRegistry] Collections Rendered (runtime discovery)", {
      ...meta,
      "Discovery Source": discoverySource,
      "Collections Discovered": list.length,
      "Collections Rendered": list.length,
      collectionNames: list.map((c) => c.collectionName),
      version: discovery.version,
    });
    return list;
  } catch (err) {
    const message = formatDiscoveryError(err);
    console.error("[collectionRegistry] runtime discovery failed", {
      ...meta,
      error: message,
      code: err?.code,
    });
    throw new Error(`Collection discovery failed: ${message}`);
  }
}

/**
 * Full rediscover — ignores registry cache as source of truth, scans runtime
 * project, replaces registry completely, returns fresh sidebar list.
 */
export async function rediscoverCollections(createdBy = null) {
  const { list, discovery, discoverySource, registryUpdated, meta } = await discoverFromActiveRuntime({
    createdBy,
    syncRegistry: true,
    replaceRegistry: false,
    discoverySource: DISCOVERY_SOURCE.MANUAL_REFRESH,
  });

  console.info("[collectionRegistry] Registry Updated (full rediscover)", {
    ...meta,
    "Discovery Source": discoverySource,
    "Collections Discovered": list.length,
    "Registry Updated": registryUpdated,
    version: discovery.version,
  });

  return {
    registeredCollections: list,
    discoverySource,
    collectionsDiscovered: list.length,
    registryUpdated,
    discovery,
    runtimeProjectId: meta.runtimeProjectId,
  };
}

export async function probeCollectionExists(collectionName) {
  requireCustomerRuntime();
  const name = normalizeName(collectionName);
  if (!name) return false;
  try {
    await getCountFromServer(collection(db, name));
    return true;
  } catch {
    try {
      await getDocs(query(collection(db, name), limit(1)));
      return true;
    } catch {
      return false;
    }
  }
}

export async function refreshCollectionStats(collectionName) {
  const name = normalizeName(collectionName);
  if (!name) return null;
  const countSnap = await getCountFromServer(collection(db, name));
  const documentCount = countSnap.data().count;
  const status = documentCount === 0 ? "empty" : "active";
  const now = new Date().toISOString();
  await setDoc(
    registryRef(name),
    { collectionName: name, documentCount, lastUpdated: now, status },
    { merge: true }
  );
  return { collectionName: name, documentCount, status, lastUpdated: now };
}

/**
 * Register or touch a collection in the registry.
 */
export async function registerCollection(collectionName, options = {}) {
  const name = normalizeName(collectionName);
  if (!name || name === REGISTRY_COLLECTION) return null;

  const {
    createdBy = null,
    documentCount = null,
    status = null,
    source = "app",
  } = options;

  const ref = registryRef(name);
  const existing = await getDoc(ref);
  const now = new Date().toISOString();

  if (existing.exists()) {
    const patch = { collectionName: name, lastUpdated: now };
    if (typeof documentCount === "number") patch.documentCount = documentCount;
    if (status) patch.status = status;
    await setDoc(ref, patch, { merge: true });
    return { ...existing.data(), ...patch, id: name };
  }

  const payload = {
    collectionName: name,
    createdAt: now,
    createdBy,
    documentCount: typeof documentCount === "number" ? documentCount : null,
    lastUpdated: now,
    status: status || "active",
    source,
  };
  await setDoc(ref, payload);
  return { id: name, ...payload };
}

export async function touchCollection(collectionName, createdBy = null) {
  return registerCollection(collectionName, { createdBy });
}

/**
 * Verify a collection exists in Firestore, then register it.
 */
export async function registerExistingCollection(collectionName, createdBy = null) {
  const name = normalizeName(collectionName);
  if (!name) throw new Error("Collection name is required");
  const exists = await probeCollectionExists(name);
  if (!exists) throw new Error(`Collection "${name}" does not exist or is not accessible`);
  const stats = await refreshCollectionStats(name);
  if (!stats) throw new Error("Could not register collection");
  const existing = await getDoc(registryRef(name));
  if (!existing.exists()) {
    await setDoc(
      registryRef(name),
      {
        createdAt: new Date().toISOString(),
        createdBy,
        source: "manual",
      },
      { merge: true }
    );
  }
  return stats;
}

/**
 * Rebuild firestore_collection_registry from Admin SDK discovery (Cloud Function).
 * Writes registry docs into the active runtime project (customer or Studio).
 */
async function syncRegistryFromAdminDiscovery(createdBy = null, options = {}) {
  const discoverySource = options.discoverySource || DISCOVERY_SOURCE.BACKGROUND_REFRESH;
  const meta = runtimeMeta();

  if (!meta.hasActiveRuntime) {
    throw new Error("No active workspace connection.");
  }

  console.info("[collectionRegistry] syncRegistryFromAdminDiscovery", {
    ...meta,
    "Discovery Engine": DISCOVERY_ENGINE,
    discoverySource,
  });

  const { list, discovery } = await discoverFromActiveRuntime({
    createdBy,
    syncRegistry: true,
    replaceRegistry: Boolean(options.replaceRegistry),
    discoverySource,
  });

  const names = discovery.collections;
  const registryWritten = discovery.registryWritten || list.length;
  const registryRead = list.length;

  const pipeline = {
    "Cloud Function Version": discovery.version,
    "Admin SDK Collections": names.length,
    "Registry Written": registryWritten,
    "Registry Read": registryRead,
    "Discovery Engine": DISCOVERY_ENGINE,
    "engine used": discovery.engine,
    "collections returned": names,
    "Discovery Source": discoverySource,
    runtimeProjectId: discovery.projectId ?? meta.runtimeProjectId,
    credentialMode: discovery.cloudFunctionResponse?.credentialMode ?? null,
  };

  console.info("[collectionDiscovery] pipeline verification", {
    ...pipeline,
    "cloud function response": discovery.cloudFunctionResponse,
    "Registry Updated": true,
  });

  return {
    synced: list.map((row) => ({ collectionName: row.collectionName })),
    source: discoverySource,
    engine: DISCOVERY_ENGINE,
    adminEngine: discovery.engine,
    version: discovery.version,
    discovered: names,
    discoveredCount: names.length,
    added: registryWritten,
    addedNames: names,
    runtimeProjectId: discovery.projectId ?? meta.runtimeProjectId,
    cloudFunctionResponse: discovery.cloudFunctionResponse,
    pipeline,
    registeredCollections: list,
    discoverySource,
    collectionsDiscovered: list.length,
  };
}

/** @deprecated Use syncRegistryFromAdminDiscovery */
export async function syncRegistryFromRuntime(createdBy = null) {
  if (!hasActiveRuntime()) {
    throw new Error("No active customer runtime — cannot discover collections.");
  }
  return syncRegistryFromAdminDiscovery(createdBy);
}

export async function getServerCollectionNames() {
  const discovery = await discoverCollectionsViaAdminSdk({
    connectionId: hasActiveRuntime() ? getActiveRuntimeConnectionId() : null,
    runtimeProjectId: getRuntimeDb()?.app?.options?.projectId ?? null,
  });
  return discovery.collections;
}

/** True when Firestore root collections exist that are not yet in the registry. */
export async function isRegistryIncomplete() {
  if (!hasActiveRuntime()) return true;
  try {
    let cache = [];
    try {
      cache = await readRegistryCacheOnly();
    } catch (err) {
      if (isPermissionDeniedError(err)) {
        console.info("[collectionRegistry] isRegistryIncomplete skipped — registry cache not readable from browser");
        return false;
      }
      throw err;
    }
    const discovered = await getServerCollectionNames();
    const regSet = new Set(cache.map((r) => r.collectionName));
    const discoveredSet = new Set(discovered);
    const incomplete =
      !cache.length ||
      discovered.some((n) => !regSet.has(n)) ||
      cache.some((r) => !discoveredSet.has(r.collectionName));
    console.info("[collectionRegistry] isRegistryIncomplete (runtime)", {
      ...runtimeMeta(),
      cache: cache.length,
      discovered: discovered.length,
      incomplete,
    });
    return incomplete;
  } catch {
    return true;
  }
}

/**
 * Sync registry from Admin SDK discovery (Cloud Function).
 * Works for both Studio default and active customer runtime connections.
 */
export async function syncRegistryFromServer(createdBy = null, options = {}) {
  return syncRegistryFromAdminDiscovery(createdBy, options);
}
